import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { lockInput, unlockInput } from './inputLock.js';
import { audioManager } from '../audio/AudioManager.js';
import { api } from '../api/client.js';
import { gameState } from '../state/gameState.js';
import { nameWithIcon } from './botLabel.mjs';
import { iconText } from './iconText.js';

// The ranking_board texture (523×720) is a framed easel sign with 7 ruled rows drawn
// into its green felt. These are the source-pixel Y positions (traced with a grid
// overlay 2026-07-09) of the nameplate title and each row's centre line — mapped to
// screen space via the image's displayed scale/top so the text sits ON the drawn lines.
const TITLE_Y = 145;
const SUBTITLE_Y = 176;
const ROW_YS = [255, 315, 375, 435, 495, 555, 617];
const MAX_ROWS = ROW_YS.length;

// Games shown as ranking tabs, in display order (mirrors GameShopModal's GAME_ORDER).
const GAME_ORDER = ['checkers', 'chess', 'go', 'mancala', 'makao', 'backgammon'];

// Ranking board wall sign (GameHallScene.js): per-game leaderboard from
// GET /api/game-hall/rankings. One tab per implemented game (Game Hall Plan 4,
// Task 5) — was hardcoded to Warcaby only; the backend aggregation was already
// game-agnostic (game_hall.py groups by game_type generically).
export function showRankingModal(scene) {
  const layer = scene.add.container(0, 0).setDepth(2000);
  lockInput(scene);

  const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2;
  const overlay = scene.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.6)
    .setOrigin(0, 0).setInteractive();
  layer.add(overlay);

  const board = scene.add.image(cx, cy, 'ranking_board');
  const scale = Math.min(560 / board.width, 660 / board.height);
  board.setScale(scale);
  layer.add(board);

  const top = cy - (board.height * scale) / 2;
  const mapY = (imgY) => top + imgY * scale;

  const catalog = gameState.raw?.catalog?.board_games ?? {};
  const implementedGames = GAME_ORDER.filter((id) => catalog[id]?.implemented);
  let activeGame = implementedGames[0] ?? 'checkers';
  let rankingsData = null;   // cached API response — tab switches re-render from this, no refetch

  layer.add(iconText(scene, cx, mapY(TITLE_Y), 'Ranking {trophy}', {
    fontFamily: 'system-ui', fontSize: '26px', color: '#2e2116', fontStyle: 'bold',
  }).setOrigin(0.5));

  const tabsLayer = scene.add.container(0, 0);
  layer.add(tabsLayer);
  const rowsLayer = scene.add.container(0, 0);
  layer.add(rowsLayer);
  const rowStyle = { fontFamily: 'system-ui', fontSize: '15px', color: '#f5eecb', fontStyle: 'bold' };

  // Single game (or none marked implemented) → plain subtitle, no tab affordance.
  // Multiple → clickable name-per-game row; the active one bold/dark, others muted.
  const renderTabs = () => {
    tabsLayer.removeAll(true);
    if (implementedGames.length <= 1) {
      const name = catalog[activeGame]?.name_pl ?? activeGame;
      tabsLayer.add(iconText(scene, cx, mapY(SUBTITLE_Y), name, {
        fontFamily: 'system-ui', fontSize: '15px', color: '#5b4636',
      }).setOrigin(0.5));
      return;
    }
    const GAP = 20;
    const items = implementedGames.map((id) => {
      const active = id === activeGame;
      const t = iconText(scene, 0, mapY(SUBTITLE_Y), catalog[id]?.name_pl ?? id, {
        fontFamily: 'system-ui', fontSize: '15px', color: active ? '#2e2116' : '#8a7a5c',
        fontStyle: active ? 'bold' : 'normal',
      }).setOrigin(0.5);
      return { id, t };
    });
    const totalW = items.reduce((sum, { t }) => sum + t.width, 0) + GAP * (items.length - 1);
    let x = cx - totalW / 2;
    for (const { id, t } of items) {
      t.setX(x + t.width / 2);
      x += t.width + GAP;
      t.setInteractive({ useHandCursor: true });
      t.on('pointerup', () => {
        if (activeGame === id) return;
        activeGame = id;
        renderTabs();
        renderRows(rankingsData?.games?.[activeGame]?.rows ?? []);
      });
      tabsLayer.add(t);
    }
  };
  renderTabs();

  const renderRows = (rows) => {
    rowsLayer.removeAll(true);
    if (!rows.length) {
      rowsLayer.add(iconText(scene, cx, mapY(ROW_YS[Math.floor(MAX_ROWS / 2)]), 'Nikt jeszcze nie zagrał!', rowStyle)
        .setOrigin(0.5));
      return;
    }
    rows.slice(0, MAX_ROWS).forEach((r, i) => {
      const best = r.best_bot ? ` · max ${r.best_bot}` : '';   // an icon token from the server ({brain} …)
      const txt = `${i + 1}. ${nameWithIcon(r.name)}  ${r.wins}W / ${r.losses}P / ${r.draws}R (bot: ${r.bot_wins}/${r.bot_losses}${best})`;
      rowsLayer.add(iconText(scene, cx, mapY(ROW_YS[i]), txt, rowStyle).setOrigin(0.5));
    });
  };

  renderRows([]);   // placeholder while the request is in flight
  api.gameHallRankings()
    .then((res) => { rankingsData = res; renderRows(res?.games?.[activeGame]?.rows ?? []); })
    .catch(() => rowsLayer.add(iconText(scene, cx, mapY(ROW_YS[Math.floor(MAX_ROWS / 2)]),
      'Nie udało się wczytać rankingu', rowStyle).setOrigin(0.5)));

  const closeBtn = iconText(scene, cx + (board.width * scale) / 2 - 6, top + 6, '×', {
    fontFamily: 'system-ui', fontSize: '22px', color: '#a33', fontStyle: 'bold',
  }).setOrigin(1, 0).setInteractive({ useHandCursor: true }).setDepth(2002);
  layer.add(closeBtn);

  const close = () => { audioManager.playSfx('click'); unlockInput(scene); layer.destroy(); };
  closeBtn.on('pointerup', close);
  overlay.on('pointerup', close);
}
